import {QueryClient, useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {
    Creative,
    SupportedCountry,
    complianceNameToCodeId,
    type ComplianceCodeName,
    createPollingGenerator
} from 'bigdatr-style';
import type {
    ComplianceShortlistCreativeQuery,
    ComplianceCsvInput,
    CreativeItemV3,
    ComplianceCreativeListSort
} from '~/graphql-react-query/graphql';
import {QueryStringFilters} from '~/feature/filter/types';
import useClientMainAuthorizedFetch from '../clientMainAuthorisedFetch';
import {ComplianceCreativeItem as ComplianceCreativeItemQuery} from '../creative/graphqlQueries';
import ensureDatesAreWithinTrialLimits from '~/creative/util/ensureDatesAreWithinTrialLimits';
import {useCountry} from '~/feature/country/CountryContext';
import useEarliestSelectableCreativeDate from '~/affordance/dates/useEarliestSelectableDate';
import {
    ComplianceAddToShortlist,
    ComplianceCreativeList,
    ComplianceCsv,
    ComplianceRemoveFromShortlist,
    ComplianceShortlistCreative as ComplianceShortlistCreativeQueryDocument,
    ComplianceShortlistCreatives,
    ComplianceShortlists
} from './graphqlQueries';
import {complianceShortlistQueryKeys} from './helpers';

type ComplianceShortlistCreative = NonNullable<
    NonNullable<ComplianceShortlistCreativeQuery['compliance']>['creative']
>;
type ComplianceShortlistCreativeMutationArgs = {
    complianceList: ComplianceCodeName;
    creativeId: string;
    shortList: string;
};

type ComplianceDataRow = NonNullable<CreativeItemV3['compliance']>[number];

function findComplianceForActiveCode(
    complianceFromCreative: CreativeItemV3['compliance'],
    activeComplianceCodeId: string
): ComplianceDataRow | undefined {
    return complianceFromCreative?.find(
        (row) => row?.compliance_code_id === activeComplianceCodeId
    );
}

async function invalidateComplianceShortlistQueries(
    queryClient: QueryClient,
    args: ComplianceShortlistCreativeMutationArgs
) {
    const complianceCodeId = complianceNameToCodeId(args.complianceList);

    await Promise.all([
        queryClient.invalidateQueries({
            queryKey: complianceShortlistQueryKeys.lists(complianceCodeId)
        }),
        queryClient.invalidateQueries({
            queryKey: complianceShortlistQueryKeys.creatives(complianceCodeId, args.shortList)
        }),
        queryClient.invalidateQueries({
            queryKey: complianceShortlistQueryKeys.creative(complianceCodeId, args.creativeId)
        })
    ]);
}

export function useComplianceResults(args: {
    complianceCodeName: ComplianceCodeName;
    filter?: QueryStringFilters;
    orderBy: ComplianceCreativeListSort;
    pageNumber: number;
    enabled?: boolean;
}) {
    const {orderBy, pageNumber, complianceCodeName, enabled = true} = args;
    const {isCreativeOnTrial} = useCountry();
    const earliestSelectableDate = useEarliestSelectableCreativeDate({type: 'creative'});
    const request = useClientMainAuthorizedFetch();

    const {creativeDateRange, ...otherFilters} = args.filter ?? {};
    const dateWithinLimitations = ensureDatesAreWithinTrialLimits({
        originalDate: creativeDateRange,
        isTrialing: isCreativeOnTrial,
        earliestSelectableDate
    });

    const filters = {
        ...otherFilters,
        country: SupportedCountry.AU,
        dateRangeFilter: dateWithinLimitations,
        compliance_code_id: complianceNameToCodeId(complianceCodeName)
    };

    const pageSize = 20;

    return useQuery({
        enabled,
        queryKey: [
            'compliance-results',
            complianceCodeName,
            otherFilters,
            filters,
            orderBy,
            pageNumber,
            pageSize
        ],
        queryFn: async () => {
            const response = await request(ComplianceCreativeList, {
                input: {
                    filters,
                    orderBy,
                    pagination: {
                        count: pageSize,
                        page: pageNumber
                    }
                }
            });
            const rawResponse = response.compliance?.creativeList;
            if (!rawResponse) throw new Error('Compliance creative list not returned from api');

            const {items, paginationInfo} = rawResponse;
            const activeComplianceCodeId = complianceNameToCodeId(complianceCodeName);
            const creatives = items.map((item) =>
                mapOnlyActiveCodeToCreative(item, activeComplianceCodeId)
            );

            return {
                creatives,
                hasNextPage: paginationInfo.hasNextPage,
                hasPreviousPage: paginationInfo.page > 1
            };
        }
    });
}

export function useComplianceCreative(args: {
    creativeId: string;
    complianceList: ComplianceCodeName;
    enabled?: boolean;
}) {
    const {creativeId, complianceList, enabled = true} = args;
    const request = useClientMainAuthorizedFetch();

    return useQuery({
        enabled,
        queryKey: ['compliance-creative', creativeId, complianceList],
        queryFn: async () => {
            const response = await request(ComplianceCreativeItemQuery, {creativeId});
            const rawCreative = response.creativeV2?.publicCreativeItemV2;
            if (!rawCreative) throw new Error('Creative not returned from api');

            return mapOnlyActiveCodeToCreative(rawCreative, complianceNameToCodeId(complianceList));
        }
    });
}

export function useComplianceShortlists(args: {
    complianceList: ComplianceCodeName;
    enabled?: boolean;
}) {
    const {complianceList, enabled = true} = args;
    const request = useClientMainAuthorizedFetch();
    const complianceCodeId = complianceNameToCodeId(complianceList);

    return useQuery({
        enabled,
        queryKey: complianceShortlistQueryKeys.lists(complianceCodeId),
        queryFn: async () => {
            const response = await request(ComplianceShortlists, {
                input: {complianceCodeId}
            });
            const shortLists = response.compliance?.shortLists;
            if (!shortLists) throw new Error('Compliance shortlists not returned from api');
            return shortLists;
        }
    });
}

export function useComplianceShortlistCreatives(args: {
    complianceList: ComplianceCodeName;
    shortList: string;
    enabled?: boolean;
}) {
    const {complianceList, shortList, enabled = true} = args;
    const request = useClientMainAuthorizedFetch();
    const complianceCodeId = complianceNameToCodeId(complianceList);

    return useQuery({
        enabled,
        queryKey: complianceShortlistQueryKeys.creatives(complianceCodeId, shortList),
        queryFn: async () => {
            const response = await request(ComplianceShortlistCreatives, {
                input: {complianceCodeId, shortList}
            });
            const creatives = response.compliance?.creatives;
            if (!creatives) {
                throw new Error('Compliance shortlist creatives not returned from api');
            }
            return creatives;
        }
    });
}

export function useComplianceShortlistCreative(args: {
    complianceList: ComplianceCodeName;
    creativeId: string;
    enabled?: boolean;
}) {
    const {complianceList, creativeId, enabled = true} = args;
    const request = useClientMainAuthorizedFetch();
    const complianceCodeId = complianceNameToCodeId(complianceList);

    return useQuery({
        enabled,
        queryKey: complianceShortlistQueryKeys.creative(complianceCodeId, creativeId),
        queryFn: async (): Promise<ComplianceShortlistCreative> => {
            const response = await request(ComplianceShortlistCreativeQueryDocument, {
                input: {complianceCodeId, creativeId}
            });
            const creative = response.compliance?.creative;
            if (!creative) {
                throw new Error('Compliance shortlist creative not returned from api');
            }
            return creative;
        }
    });
}

export function useComplianceAddToShortlist() {
    const request = useClientMainAuthorizedFetch();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (args: ComplianceShortlistCreativeMutationArgs) => {
            const {complianceList, creativeId, shortList} = args;
            const response = await request(ComplianceAddToShortlist, {
                input: {
                    complianceCodeId: complianceNameToCodeId(complianceList),
                    creativeId,
                    shortList
                }
            });
            return response.compliance?.addToShortlist ?? null;
        },
        onSuccess: async (_data, args) => {
            await invalidateComplianceShortlistQueries(queryClient, args);
        }
    });
}

export function useComplianceRemoveFromShortlist() {
    const request = useClientMainAuthorizedFetch();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (args: ComplianceShortlistCreativeMutationArgs) => {
            const {complianceList, creativeId, shortList} = args;
            const response = await request(ComplianceRemoveFromShortlist, {
                input: {
                    complianceCodeId: complianceNameToCodeId(complianceList),
                    creativeId,
                    shortList
                }
            });
            return response.compliance?.removeFromShortlist ?? null;
        },
        onSuccess: async (_data, args) => {
            await invalidateComplianceShortlistQueries(queryClient, args);
        }
    });
}

export function useComplianceCsv() {
    const request = useClientMainAuthorizedFetch();

    return useMutation({
        mutationFn: async (input: ComplianceCsvInput) => {
            const pollingData = createPollingGenerator({
                createRequest: async () => {
                    const response = await request(ComplianceCsv, {input});
                    return response.compliance?.csv;
                },
                pollRequest: async (response) => {
                    const {csv, errorJson} = response || {};
                    if (!csv || !errorJson) {
                        throw new Error('CSV or error JSON signed URLsnot returned from API');
                    }

                    // make a request to the signed URLs and return their statuses
                    const [csvResponse, errorJsonResponse] = await Promise.all([
                        fetch(csv),
                        fetch(errorJson)
                    ]);

                    const hasError = errorJsonResponse.status === 200;
                    const isCsvFileReady = csvResponse.status === 200;
                    return {
                        csv,
                        errorJson,
                        isCsvFileReady,
                        downloadStatus: csvResponse.status,
                        errorStatus: errorJsonResponse.status,
                        hasError
                    };
                },
                pollWhile: ({isCsvFileReady, hasError}) => {
                    // stop polling if an error file shows up, or if the csv file shows up
                    return !hasError && !isCsvFileReady;
                },
                interval: 2000
            });

            let lastPayload: Awaited<ReturnType<(typeof pollingData)['next']>>['value'] | null =
                null;
            for await (const payload of pollingData) {
                lastPayload = payload;
                // if an error file exists at the given URL, fetch the JSON with the error message
                // and expose it to the user by throwing it
                if (payload.hasError) {
                    const errorResponse = await fetch(payload.errorJson);
                    const json = await errorResponse.json();
                    const error = new Error(json.error);
                    error.name = json.name;
                    error.stack = json.stack;
                    throw error;
                }
            }
            return lastPayload;
        }
    });
}

function mapOnlyActiveCodeToCreative(
    item: CreativeItemV3,
    activeComplianceCodeId: string
): Creative {
    const {compliance, ...rest} = item;
    const row = findComplianceForActiveCode(compliance, activeComplianceCodeId);
    return new Creative({
        ...rest,
        complianceRanking: row?.compliance_ranking ?? null,
        complianceReason: row?.compliance_reason ?? null
    });
}
